import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ACCESS_TOKEN,
  useBodyCssClass,
  useDidMount,
  useEventListener,
  useLocalStorage,
  useLocationChange,
} from '@hsp/react-hooks';
import { IAuthenticationToken, isNotDefined } from '@hsp/utilities';

import { customEvents } from 'constants/custom-events';
import { useAppDispatch, useAppSelector, useResetAudienceStore } from 'hooks/store';
import { useRefreshToken } from 'hooks/token';
import { ApplicationActions } from 'store/actions';
import { selectIsAuthenticated } from 'store/selectors';
import { IErrorResult } from 'types/api/common';
import { notifications } from 'utilities/notifications';
import { buildUrl } from 'utilities/strings';

export const useConfiguration = () => {
  const dispatch = useAppDispatch();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const [, setAccessToken] = useLocalStorage<IAuthenticationToken>(ACCESS_TOKEN, null);
  const navigate = useNavigate();
  const refreshToken = useRefreshToken();
  const resetAudienceStore = useResetAudienceStore();

  const handleApiError = useCallback((e: CustomEvent<IErrorResult>) => {
    const { detail } = e;

    if (isNotDefined(detail)) {
      return;
    }

    notifications.error(detail.message);
  }, []);
  const handleLogOff = useCallback(
    (e: CustomEvent<boolean>) => {
      const { detail: revoke = false } = e;

      navigate(buildUrl('Root.children.Logout'), { replace: true, state: { revoke } });
    },
    [navigate]
  );
  const handleNewToken = useCallback(
    (e: CustomEvent<IAuthenticationToken>) => {
      const { detail } = e;

      setAccessToken(detail);
    },
    [setAccessToken]
  );
  const handleRefreshToken = useCallback(() => refreshToken(), [refreshToken]);

  useBodyCssClass('theme-hsp-studio');

  useDidMount(() => {
    dispatch(ApplicationActions.getPasswordRules());
  });

  useLocationChange((current, previous) => resetAudienceStore(current, previous));

  useEventListener({
    eventName: customEvents.apiErrorEvent,
    handler: handleApiError,
  });

  useEventListener({
    eventName: customEvents.logOffEvent,
    handler: handleLogOff,
  });

  useEventListener({
    eventName: customEvents.newTokenEvent,
    handler: handleNewToken,
  });

  useEventListener({
    eventName: customEvents.refreshToken,
    handler: handleRefreshToken,
  });

  return {
    isAuthenticated,
  } as const;
};
